import { DestroyRef, Injectable, Provider, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { L10nTranslationService } from 'angular-l10n';

/**
 * `mat-paginator`'s own labels, translated.
 *
 * Material ships them hardcoded in English ("Items per page:", "1 – 10 of 95"), so every paginator
 * in the app leaks English regardless of the active locale. This reads them from the `$paginator.*`
 * catalogue instead, and re-emits `changes` when the locale switches so paginators already on
 * screen pick the new strings up rather than keeping the ones they read on first render.
 */
@Injectable()
export class WbiPaginatorIntl extends MatPaginatorIntl {
  private readonly translation = inject(L10nTranslationService);

  constructor() {
    super();
    this.applyLabels();
    this.translation
      .onChange()
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => {
        this.applyLabels();
        this.changes.next();
      });
  }

  /**
   * Material calls this on every render, so it reads the catalogue live rather than caching — the
   * numbers are the same arithmetic as Material's own default, only the wording is ours.
   */
  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const total = Math.max(length, 0);
    const start = total === 0 || pageSize === 0 ? 0 : page * pageSize;
    const end = total === 0 ? 0 : Math.min(start + pageSize, total);

    return `${total === 0 ? 0 : start + 1} – ${end} ${this.t('of')} ${total}`;
  };

  private applyLabels(): void {
    this.itemsPerPageLabel = this.t('items-per-page');
    this.firstPageLabel = this.t('first-page');
    this.previousPageLabel = this.t('previous-page');
    this.nextPageLabel = this.t('next-page');
    this.lastPageLabel = this.t('last-page');
  }

  private t(key: string): string {
    return this.translation.translate(`$paginator.${key}`);
  }
}

/** Wires the translated labels into every `mat-paginator` of the app that injects this. */
export function provideWbiPaginatorIntl(): Provider {
  return { provide: MatPaginatorIntl, useClass: WbiPaginatorIntl };
}
